import {
  ActionIcon,
  Button,
  Code,
  Collapse,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconHome,
  IconRefresh,
} from "@tabler/icons-react";
import { useState } from "react";

export interface ErrorPageProps {
  /**
   * 에러 화면 메인 제목 (기본값: "서비스 이용에 불편을 드려 죄송합니다")
   */
  title?: string;
  /**
   * 에러 화면 부가 설명 문구
   */
  message?: string;
  /**
   * 발생한 에러 객체 (메시지 및 스택 표시용)
   */
  error?: Error | unknown;
  /**
   * 새로고침 버튼 클릭 핸들러 (기본값: window.location.reload)
   */
  onReload?: () => void;
  /**
   * 홈으로 이동 버튼 클릭 핸들러
   */
  onGoHome?: () => void;
  /**
   * 이전 페이지로 이동 버튼 클릭 핸들러
   */
  onGoBack?: () => void;
  /**
   * 홈 경로 (기본값: "/")
   */
  homePath?: string;
  /**
   * 오류 세부 정보 접이식 영역 노출 여부 (기본값: true)
   */
  showDetails?: boolean;
  /**
   * 오류 화면에 표시할 서비스 이름 (기본값: "DutyOn")
   */
  serviceName?: string;
  /**
   * 서비스 로고 이미지 경로
   */
  logoSrc?: string;
  /**
   * 홈 이동 버튼 문구 (기본값: "홈으로 이동")
   */
  homeLabel?: string;
}

export function ErrorPage({
  title = "서비스 이용에 불편을 드려 죄송합니다",
  message = "예기치 못한 문제가 발생하여 페이지를 표시할 수 없습니다.\n잠시 후 다시 시도해 주시거나 아래 버튼을 통해 이동해 주세요.",
  error,
  onReload,
  onGoHome,
  onGoBack,
  homePath = "/",
  showDetails = true,
  serviceName = "DutyOn",
  logoSrc,
  homeLabel = "홈으로 이동",
}: ErrorPageProps) {
  const [opened, { toggle }] = useDisclosure(false);
  const [copied, setCopied] = useState(false);

  const handleReload = () => {
    if (onReload) {
      onReload();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = homePath;
    }
  };

  const handleGoBack = () => {
    if (onGoBack) {
      onGoBack();
    } else if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
    } else {
      handleGoHome();
    }
  };

  const errorMessage =
    error instanceof Error ? error.message : typeof error === "string" ? error : "Unknown error";

  const errorStack = error instanceof Error ? error.stack : null;
  const handleCopyDetails = () => {
    const textToCopy = `Error: ${errorMessage}\n\nStack:\n${errorStack || "No stack trace available"}`;
    if (navigator?.clipboard?.writeText) {
      void navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(180deg, #f8fafc 0%, #f3f6fb 100%)",
        padding: "clamp(1rem, 4vw, 3rem)",
      }}
    >
      <Container size="sm" px={0} style={{ width: "100%" }}>
        <Paper
          p={{ base: "xl", sm: 40 }}
          radius="xl"
          style={{
            width: "100%",
            background: "rgba(255, 255, 255, 0.96)",
            borderColor: "rgba(226, 232, 240, 0.95)",
            boxShadow: "0 24px 70px rgba(15, 23, 42, 0.1)",
          }}
        >
          <Stack align="center" gap="xl">
            <Group gap="sm" wrap="nowrap">
              {logoSrc && (
                <img
                  src={logoSrc}
                  alt=""
                  aria-hidden="true"
                  width={40}
                  height={40}
                  style={{ display: "block", objectFit: "contain" }}
                />
              )}
              <Text fw={900} size="lg" c="blue">
                {serviceName}
              </Text>
            </Group>

            <ThemeIcon
              size={76}
              radius="xl"
              variant="light"
              color="blue"
              style={{ boxShadow: "0 12px 28px rgba(34, 139, 230, 0.16)" }}
            >
              <IconAlertTriangle size={38} stroke={1.8} />
            </ThemeIcon>

            <Stack align="center" gap="xs" style={{ textAlign: "center" }}>
              <Title
                order={2}
                style={{ fontSize: "clamp(1.45rem, 4vw, 1.85rem)", wordBreak: "keep-all" }}
              >
                {title}
              </Title>
              <Text
                c="dimmed"
                size="md"
                maw={480}
                style={{ whiteSpace: "pre-line", wordBreak: "keep-all", lineHeight: 1.7 }}
              >
                {message}
              </Text>
            </Stack>

            <Group justify="center" gap="sm" wrap="wrap" style={{ width: "100%" }}>
              <Button
                size="md"
                color="blue"
                leftSection={<IconRefresh size={18} />}
                onClick={handleReload}
                style={{ boxShadow: "0 10px 22px rgba(34, 139, 230, 0.2)" }}
              >
                다시 불러오기
              </Button>
              <Button
                size="md"
                variant="default"
                leftSection={<IconHome size={18} />}
                onClick={handleGoHome}
              >
                {homeLabel}
              </Button>
              {onGoBack !== null && (
                <Button
                  size="md"
                  variant="subtle"
                  color="gray"
                  leftSection={<IconArrowLeft size={18} />}
                  onClick={handleGoBack}
                >
                  이전 페이지
                </Button>
              )}
            </Group>

            {showDetails && Boolean(error) && (
              <Paper
                withBorder
                radius="lg"
                p="sm"
                style={{ width: "100%", boxShadow: "none" }}
                bg="var(--mantine-color-gray-0)"
              >
                <Stack gap="xs">
                  <Group justify="space-between" wrap="nowrap">
                    <Group
                      gap="xs"
                      wrap="nowrap"
                      style={{ cursor: "pointer", minWidth: 0, flex: 1 }}
                      onClick={toggle}
                    >
                      <Text size="xs" fw={700} c="dimmed" style={{ whiteSpace: "nowrap" }}>
                        오류 세부 정보
                      </Text>
                      <Text size="xs" c="gray" fw={500} style={{ minWidth: 0 }} truncate>
                        {errorMessage}
                      </Text>
                    </Group>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={opened ? "오류 세부 정보 닫기" : "오류 세부 정보 열기"}
                      onClick={toggle}
                    >
                      {opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                    </ActionIcon>
                  </Group>

                  <Collapse in={opened}>
                    <Stack gap="xs" mt="xs">
                      <Group justify="space-between" align="center" wrap="nowrap">
                        <Text size="xs" c="dimmed">
                          문제가 지속되면 아래 내용을 복사하여 기술 지원팀에 문의해 주세요.
                        </Text>
                        <Tooltip label={copied ? "복사되었습니다!" : "오류 내용 복사"}>
                          <ActionIcon
                            variant="light"
                            color={copied ? "teal" : "blue"}
                            size="sm"
                            onClick={handleCopyDetails}
                          >
                            {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                      <Code
                        block
                        style={{
                          maxHeight: 180,
                          overflowY: "auto",
                          fontSize: "0.75rem",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                        }}
                      >
                        {errorStack || errorMessage}
                      </Code>
                    </Stack>
                  </Collapse>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Paper>
      </Container>
    </div>
  );
}
